/**
 * Account Service
 *
 * Accounts have a more complex backend than a typical RA resource:
 *   - List via `/account-with-pagination` (handled by the generic dataProvider)
 *   - Read  via `/account/{id}`                (handled by dataProvider)
 *   - Delete via `/account/{id}`               (handled by dataProvider)
 *   - Create via POST `/account`               (this service)
 *   - Update via PUT  `/account/{id}`          (this service — non-standard URL)
 *
 * Plus three cross-resource helpers used by the Applications tab:
 *   - GET /application                         (all Notify apps)
 *   - GET /application/templatify-applications (all Templatify apps)
 *   - GET /application/sync-status             (per-row status probe)
 *
 * And one Templatify discovery endpoint for the "Link existing account" flow:
 *   - GET /account/templatify-accounts
 *
 * Cost Centers, Licenses, Providers are loaded via their own resources and
 * reused here — no service duplication.
 */

import { httpClient } from '@/utils/httpClient';
import { emitResourceMutated } from '@/utils/resourceEvents';
import type { NotifyEnvelope } from '@/types/api';
import type { ScopeCatalogEntry } from '../../user-management/types/user';
import type {
  AccountDetailResponse,
  ApplicationRow,
  ApplicationRowOption,
  ApplicationSyncStatus,
  ApplicationTag,
  TemplatifyExistingAccount,
} from '../types/account';

interface SyncStatusResponse {
  status: ApplicationSyncStatus;
}

interface ApplicationListItem {
  id: string;
  name: string;
  tag?: ApplicationTag;
}

const inflightAccountFetches = new Map<string, Promise<AccountDetailResponse>>();

export const getAccountById = (
  id: string
): Promise<AccountDetailResponse> => {
  const hit = inflightAccountFetches.get(id);
  if (hit) return hit;
  const promise = (async () => {
    const { data } = await httpClient.get<NotifyEnvelope<AccountDetailResponse>>(
      `/account/${encodeURIComponent(id)}`
    );
    return data.Response;
  })();
  inflightAccountFetches.set(id, promise);
  promise.finally(() => inflightAccountFetches.delete(id));
  return promise;
};

export const createAccount = async (body: unknown): Promise<void> => {
  await httpClient.post<NotifyEnvelope<unknown> | undefined>('/account', body);
  emitResourceMutated('apps/accounts');
};

export const linkAccountToRegistry = async (id: string, directoryId: string): Promise<void> => {
  await httpClient.put<unknown>(`/account/${encodeURIComponent(id)}/directory`, { directoryId });
  emitResourceMutated('apps/accounts');
};

export const updateAccount = async (id: string, body: unknown): Promise<void> => {
  await httpClient.put<NotifyEnvelope<unknown> | undefined>(
    `/account/${encodeURIComponent(id)}`,
    body
  );
  emitResourceMutated('apps/accounts');
};

export const getAllNotifyApplications = async (): Promise<ApplicationRowOption[]> => {
  const { data } = await httpClient.get<NotifyEnvelope<ApplicationListItem[]>>(
    '/application',
    { skipGlobalLoader: true }
  );
  return data.Response ?? [];
};

export const getAllTemplatifyApplications = async (): Promise<ApplicationRowOption[]> => {
  const { data } = await httpClient.get<NotifyEnvelope<ApplicationListItem[]>>(
    '/application/templatify-applications',
    { skipGlobalLoader: true }
  );
  return data.Response ?? [];
};

export const getApplicationSyncStatus = async (
  row: ApplicationRow
): Promise<ApplicationSyncStatus> => {
  const qs = new URLSearchParams({
    notify: String(row.notify),
    templatify: String(row.templatify),
    applicationId: String(row.applicationId),
    options: String(row.options),
    status: String(row.status),
    name: String(row.name),
    tag: String(row.tag),
    isApplicationIntegration: String(row.isApplicationIntegration),
  }).toString();
  const { data } = await httpClient.get<NotifyEnvelope<SyncStatusResponse>>(
    `/application/sync-status?${qs}`
  );
  return data.Response?.status ?? '';
};

export const getTemplatifyAccounts = async (): Promise<TemplatifyExistingAccount[]> => {
  const { data } = await httpClient.get<
    NotifyEnvelope<TemplatifyExistingAccount[]>
  >('/account/templatify-accounts', { skipGlobalLoader: true });
  return data.Response ?? [];
};

export interface TemplatifyGroupOption { id: string; name: string }

export interface TemplatifyAccountDetails {
  id: string;
  accountName: string;
  isGlobalTemplateAccess: boolean;
  group: TemplatifyGroupOption | null;
  applications: Array<{ id: string; name: string }>;
}

export const getTemplatifyGroups = async (): Promise<TemplatifyGroupOption[]> => {
  const { data } = await httpClient.get<NotifyEnvelope<TemplatifyGroupOption[]>>(
    '/account/templatify-groups',
    { skipGlobalLoader: true }
  );
  return data.Response ?? [];
};

export interface TemplatifyTemplateOption {
  id: string;
  templateName: string;
  templateType?: string;
  language?: string;
}

/**
 * Proves the account-name + api-key pair works against Templatify by
 * listing that account's templates with those credentials.
 */
export const validateTemplatifyCredentials = async (
  accountName: string,
  apiKey: string
): Promise<TemplatifyTemplateOption[]> => {
  const { data } = await httpClient.post<NotifyEnvelope<TemplatifyTemplateOption[]>>(
    '/account/templatify-validate-credentials',
    { accountName, apiKey },
    { skipGlobalLoader: true }
  );
  return data.Response ?? [];
};

/** Full Templatify account card — the "validate" action when linking. */
export const getTemplatifyAccountDetails = async (
  templatifyAccountId: string
): Promise<TemplatifyAccountDetails> => {
  const { data } = await httpClient.get<NotifyEnvelope<TemplatifyAccountDetails>>(
    `/account/templatify-account-details/${encodeURIComponent(templatifyAccountId)}`,
    { skipGlobalLoader: true }
  );
  return data.Response as TemplatifyAccountDetails;
};

export interface LicenseOption { id: string; name: string }
export interface ProviderOption {
  id: string;
  providerName: string;
  /** Canales que atiende, en camelCase ('email', 'telegram'...). El motor
   *  filtra por canal antes de recorrer la lista de prioridad. */
  communicationType?: string[];
}
export interface CostCenterOption { id: string; name: string }

/**
 * El catalogo de permisos, con el producto que habilita cada uno.
 *
 * Se pide al backend en vez de mantenerlo aqui. Antes esta pantalla tenia su propia copia
 * -la lista Y la regla de que puede repartir cada cuenta segun lo contratado-, y cuando se
 * anadio un permiso nuevo llego al backend pero aqui no aparecio la casilla, sin ningun
 * error que lo delatara. Un catalogo copiado falla en silencio; pedido, no.
 */
export const getScopeCatalog = async (): Promise<ScopeCatalogEntry[]> => {
  const { data } = await httpClient.get<NotifyEnvelope<{ scopes: ScopeCatalogEntry[] }>>(
    '/v1/scopes',
    { skipGlobalLoader: true },
  );
  return data.Response?.scopes ?? [];
};

export const getAllLicenses = async (): Promise<LicenseOption[]> => {
  const { data } = await httpClient.get<NotifyEnvelope<LicenseOption[]>>('/license', { skipGlobalLoader: true });
  return data.Response ?? [];
};

export const getAllProviders = async (): Promise<ProviderOption[]> => {
  const { data } = await httpClient.get<NotifyEnvelope<ProviderOption[]>>('/provider', { skipGlobalLoader: true });
  return data.Response ?? [];
};

export const getAllCostCenters = async (): Promise<CostCenterOption[]> => {
  const { data } = await httpClient.get<NotifyEnvelope<CostCenterOption[]>>('/costcenter', { skipGlobalLoader: true });
  return data.Response ?? [];
};

// ---- SSO users of an account (Keycloak group membership; admin 1.0.26) ----

export interface AccountSsoUser {
  keycloakId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  enabled: boolean;
  /** Permisos efectivos en ESTA cuenta, ya recortados a lo contratado. */
  scopes?: string[];
  /** false = nadie le ha configurado permisos aqui todavia; los de arriba vienen del legado. */
  scopesConfigured?: boolean;
}

export const listAccountSsoUsers = async (accountId: string): Promise<AccountSsoUser[]> => {
  const { data } = await httpClient.get<NotifyEnvelope<AccountSsoUser[]>>(
    `/account/${accountId}/sso-users`,
    { skipGlobalLoader: true },
  );
  return data.Response ?? [];
};

export const createAccountSsoUser = async (
  accountId: string,
  body: {
    email: string;
    name?: string;
    surname?: string;
    password?: string;
    phone?: string;
    telegramChatId?: string;
    scopes?: string[];
    canUseContactCenterAi?: boolean;
    canEditKnowledgeBase?: boolean;
  },
): Promise<AccountSsoUser> => {
  const { data } = await httpClient.post<NotifyEnvelope<AccountSsoUser>>(
    `/account/${accountId}/sso-users`,
    body,
  );
  return data.Response as AccountSsoUser;
};

/** Lo que la cuenta permite repartir y cuanto le queda de cupo de usuarios. */
export interface AccountScopeOptions {
  portalEnabled: boolean;
  contactCenterEnabled: boolean;
  automationsEnabled: boolean;
  maxUsers: number | null;
  currentUsers: number;
  allowedScopes: string[];
}

export const getAccountScopeOptions = async (
  accountId: string,
): Promise<AccountScopeOptions> => {
  const { data } = await httpClient.get<NotifyEnvelope<AccountScopeOptions>>(
    `/account/${accountId}/sso-users/scope-options`,
    { skipGlobalLoader: true },
  );
  return data.Response as AccountScopeOptions;
};

export const updateAccountSsoUser = async (
  accountId: string,
  keycloakUserId: string,
  body: {
    name?: string;
    surname?: string;
    phone?: string;
    telegramChatId?: string;
    scopes?: string[];
    enabled?: boolean;
  },
): Promise<AccountSsoUser> => {
  const { data } = await httpClient.put<NotifyEnvelope<AccountSsoUser>>(
    `/account/${accountId}/sso-users/${keycloakUserId}`,
    body,
  );
  return data.Response as AccountSsoUser;
};

export const deleteAccountSsoUser = async (
  accountId: string,
  keycloakUserId: string,
): Promise<void> => {
  await httpClient.delete<NotifyEnvelope<unknown> | undefined>(
    `/account/${accountId}/sso-users/${keycloakUserId}`,
  );
};
